/**
 * 否定词库表格列配置
 * 限制: <150行
 */
import React from 'react';
import { Tag, Switch, Space, Tooltip, Button, Popconfirm } from 'antd';
import { DeleteOutlined, StopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
    NegativeKeyword,
    NEGATIVE_TYPE_OPTIONS,
    NEGATIVE_REASON_OPTIONS,
    NegativeType,
    NegativeReason,
} from './types';


// 否定类型标签颜色
export const getTypeColor = (type: NegativeType) => {
    switch (type) {
        case 'asin': return 'purple';
        case 'keyword_exact': return 'red';
        case 'keyword_phrase': return 'orange';
        default: return 'default';
    }
};

// 否定原因标签颜色
export const getReasonColor = (reason: NegativeReason) => {
    switch (reason) {
        case 'irrelevant': return 'default';
        case 'low_conversion': return 'orange';
        case 'high_acos': return 'red';
        case 'competitor': return 'purple';
        case 'brand_protect': return 'blue';
        default: return 'default';
    }
};

interface GetColumnsParams {
    onUpdate: (id: number, data: Partial<NegativeKeyword>) => Promise<any>;
    onDelete: (id: number) => Promise<any>;
}

export const getNegativeKeywordColumns = ({
    onUpdate,
    onDelete,
}: GetColumnsParams): ColumnsType<NegativeKeyword> => [
        {
            title: '否定内容',
            dataIndex: 'content',
            key: 'content',
            width: 200,
            ellipsis: true,
            render: (text: string, record: NegativeKeyword) => (
                <div>
                    <Tooltip title={text}>
                        <span style={{ fontWeight: 500 }}>
                            {record.is_asin_negative && <StopOutlined style={{ color: '#722ed1', marginRight: 6 }} />}
                            {text}
                        </span>
                    </Tooltip>
                </div>
            ),
        },
        {
            title: '否定类型',
            dataIndex: 'negative_type',
            key: 'negative_type',
            width: 130,
            render: (type: NegativeType) => (
                <Tag color={getTypeColor(type)}>
                    {NEGATIVE_TYPE_OPTIONS[type]}
                </Tag>
            ),
        },
        {
            title: '原因',
            dataIndex: 'reason',
            key: 'reason',
            width: 100,
            render: (reason: NegativeReason) => (
                <Tag color={getReasonColor(reason)}>
                    {NEGATIVE_REASON_OPTIONS[reason]}
                </Tag>
            ),
        },
        {
            title: '级别',
            dataIndex: 'level_display',
            key: 'level',
            width: 110,
        },
        {
            title: 'Campaign',
            dataIndex: 'campaign_name',
            key: 'campaign_name',
            width: 120,
            ellipsis: true,
            render: (text: string) => text || '-',
        },
        {
            title: '历史数据',
            key: 'historical',
            width: 150,
            render: (_: any, record: NegativeKeyword) => (
                <Space size={4}>
                    {record.historical_clicks != null && (
                        <Tooltip title="历史点击">
                            <Tag>{record.historical_clicks} 点击</Tag>
                        </Tooltip>
                    )}
                    {record.historical_orders != null && (
                        <Tooltip title="历史订单">
                            <Tag color="green">{record.historical_orders} 单</Tag>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: '状态',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 70,
            align: 'center',
            render: (active: boolean, record: NegativeKeyword) => (
                <Switch
                    size="small"
                    checked={active}
                    onChange={(checked) => onUpdate(record.id, { is_active: checked })}
                />
            ),
        },
        {
            title: '操作',
            key: 'actions',
            width: 80,
            fixed: 'right',
            render: (_: any, record: NegativeKeyword) => (
                <Space size="small">
                    <Popconfirm
                        title="确定删除?"
                        onConfirm={() => onDelete(record.id)}
                    >
                        <Tooltip title="删除">
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];
